/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { sha256 } from 'sha.js';

import { Variable } from '../../../../ts/action';
import { ConvoSkill, Slot } from '../../../../ts/convo-skill';
import { Entity } from '../../../../ts/state';
import { ConvoSkillDraft } from '../../hooks/useConvoSkillDraft';
import { generateSlotId } from '../../util/actionUtil';

// Formula suggested by Store for uniqueness
//  when creating a new ConvoSkillAction we include this to associate to a skill version
export const generateRevisionHash = (
  conversational_skill_id: string,
  modified: string
): string => {
  const hashOb = {
    conversational_skill_id,
    modified
  };
  const hashSourceString = JSON.stringify(hashOb);
  const retVal = new sha256().update(hashSourceString).digest('hex');
  return retVal;
};

interface TransactionOperationSection {
  type: string;
  operation: string;
  payload: {};
}

/**
 *  At initial release, all Assistants that enable a ConvoSkillAction must have ALL
 *  of the available system-entities in use in the Assistant (for proper slot-fill
 *  operation).  Note it is also critical not to activate any sys-ent that is already
 *  in use as it will fail the transaction.
 *
 * @param currentEnabledSystemEntities the set of sys-ent currently enabled on this skill
 * @param allSystemEntities the set of all system entities available for use
 * @returns an array of create-sys-ent operations to enable any sys-ent not already in use
 */
export const generateNeededSysEntOps = (
  currentEnabledSystemEntities: Entity[],
  allSystemEntities: Entity[]
): TransactionOperationSection[] => {
  const retVal: TransactionOperationSection[] = [];
  const neededSystemEntities = allSystemEntities.filter(
    allEntry =>
      !currentEnabledSystemEntities.some(
        enabledEnt => allEntry.entity === enabledEnt.entity
      )
  );
  neededSystemEntities.forEach(entity => {
    const op = {
      type: 'entity',
      operation: 'create',
      payload: {
        entity: entity.entity,
        type: 'system',
        values: [],
        operation: 'create'
      },
      target_reference: entity.entity
    };
    retVal.push(op);
  });
  return retVal;
};

export const generateCreateConvoSkillBody = (
  draft: ConvoSkillDraft,
  currentEnabledSystemEntities: Entity[],
  allSystemEntities: Entity[],
  slots: Slot[],
  variables: Variable[]
): any => {
  const exampleArray = Object.values(draft.examples); // value form {text: '<exText>', created:Date modified:Date}

  // if currentEnabledSystemEntities !== allSystemEntities, then generateNeededSysEntOps
  // will return the json operations to create the remainder
  // if all sys-ents are already present, this will be empty []
  const creationOperations = generateNeededSysEntOps(
    currentEnabledSystemEntities,
    allSystemEntities
  );
  const op1 = {
    type: 'intent',
    operation: 'create',
    payload: {
      intent: draft.intentId,
      operation: 'create',
      examples: exampleArray
    },
    target_reference: draft.intentId
  };
  const op2 = {
    type: 'action',
    operation: 'create',
    payload: {
      title: draft.name.substring(0, 64),
      id: draft.actionId,
      action: draft.actionId,
      description: draft.description,
      type: 'conversational_skill',
      target_reference: draft.actionId,
      next_action: draft.nextActionId,
      condition: draft.conditions,
      registration: {
        conversational_skill_revision_id: draft.convoSkillRevisionHash,
        conversational_skill_id: draft.convoSkillId,
        provider_id: draft.providerId
      },
      launch_mode: draft.responseMode,
      disambiguation_opt_out: !draft.isDisambiguationEnabled,
      topic_switch: {
        allowed_from: draft.isChangeTopicEnabled,
        allowed_into: draft.isChangeTopicEnabled,
        never_return: draft.isNeverReturnEnabled
      },
      options: {},
      async: true,
      slots,
      variables
    }
  };
  creationOperations.push(op1);
  creationOperations.push(op2);
  return {
    operations: creationOperations
  };
};

export const generateUpdateConvoSkillBody = (draft: ConvoSkillDraft): any => {
  const exampleArray = Object.values(draft.examples); // value form {text: '<exText>', created:Date modified:Date}
  const op1 = {
    type: 'intent',
    operation: 'update',
    payload: {
      intent: draft.intentId,
      operation: 'update',
      examples: exampleArray
    },
    target_reference: draft.intentId
  };
  const op2 = {
    type: 'action',
    operation: 'update',
    payload: {
      title: draft.name.substring(0, 64),
      ...(draft?.displayName && { display_name: draft?.displayName }),
      action: draft.actionId,
      id: draft.actionId,
      description: draft.description,
      condition: draft.conditions,
      launch_mode: draft.responseMode,
      disambiguation_opt_out: !draft.isDisambiguationEnabled,
      topic_switch: {
        allowed_from: draft.isChangeTopicEnabled,
        allowed_into: draft.isChangeTopicEnabled,
        never_return: draft.isNeverReturnEnabled
      }
    },
    target_reference: draft.actionId
  };
  return {
    operations: [op1, op2]
  };
};

enum SLOT_DATA_TYPE {
  ANY = 'any',
  CONFIRMATION = 'confirmation',
  DATE = 'date',
  ENTITY = 'entity',
  NUMBER = 'number',
  REGEX = 'regex',
  STRING = 'string',
  TIME = 'time'
}

enum VARIABLE_DATA_TYPE {
  ANY = 'any',
  DATE = 'date',
  NUMBER = 'number',
  STRING = 'string',
  TIME = 'time'
}

export const mapSlotToVarType = type =>
  type && Object.values(VARIABLE_DATA_TYPE).includes(type)
    ? type
    : VARIABLE_DATA_TYPE.ANY;

export const enforceFieldSizeForSLots = (title: string, n: number) =>
  title.length > n ? `${title.substring(0, n - 3)}...` : title;

export const mapSlotDescToVarTitle = (title: string) =>
  enforceFieldSizeForSLots(title, 64);

export const mapSlotsAndVariables = (
  convoSkill: ConvoSkill
): { slots: Slot[]; variables: Variable[] } => {
  const slots = [];
  const variables = [];

  convoSkill?.input?.slots?.forEach(slot => {
    const slotType =
      slot?.type && Object.values(SLOT_DATA_TYPE).includes(slot.type)
        ? slot.type
        : SLOT_DATA_TYPE.ANY;
    const varType = mapSlotToVarType(slot?.type);
    const slotId = generateSlotId();
    slots.push({
      ...slot,
      description: enforceFieldSizeForSLots(slot.description, 512),
      name: enforceFieldSizeForSLots(slot.name, 64),
      variable: slotId,
      type: slotType
    });
    variables.push({
      title: mapSlotDescToVarTitle(slot.name),
      variable: slotId,
      data_type: varType
    });
  });
  return { slots, variables };
};
